import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Project, TaskStatus } from '../../../types';
import { dvList } from '../../../api/dataverseClient';
import { listPoolMembers, ResourcePoolMember } from '../../../services/resourcePoolService';
import {
  computeWorkload, monFriWorkingDay, MemberWorkload, WorkloadTask, WorkCapacity,
} from '../../../services/resourceWorkloadService';
import { cellColors, WEEK_COL_W, NAME_COL_W } from '../../ResourceWorkloadView';
import {
  resolveProjectCalendars, isWorkingDayFor, ResolvedCalendar, FALLBACK_HOURS_PER_DAY,
} from '../../../services/calendarService';

// Task shape for this tab: allocation fields (WorkloadTask) + the three-field
// effort model + assignee name + status for the breakdown/active filtering.
interface ProjTask extends WorkloadTask {
  dagrun_pm_assigneename: string | null;
  dagrun_pm_effortcompletedhours: number | null;
  dagrun_pm_effortremaininghours: number | null;
  dagrun_pm_status: number | null;
}
const TASK_FIELDS = [
  'dagrun_pm_taskid', 'dagrun_pm_name', 'dagrun_pm_assigneeid', 'dagrun_pm_assigneename',
  'dagrun_pm_estimatedhours', 'dagrun_pm_effortcompletedhours', 'dagrun_pm_effortremaininghours',
  'dagrun_pm_startdate', 'dagrun_pm_duedate', 'dagrun_pm_status', '_dagrun_pm_projectid_value',
];

interface Resource { userId: string; name: string; source: 'pool' | 'local' }

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

export default function ResourcesTab({ pca, projectId, project }: { pca: IPublicClientApplication; projectId: string; project: Project }) {
  const [tasks, setTasks] = useState<ProjTask[]>([]);
  const [poolMembers, setPoolMembers] = useState<ResourcePoolMember[]>([]);
  const [calendars, setCalendars] = useState<Map<string, ResolvedCalendar>>(new Map());
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'allocation' | 'breakdown'>('allocation');
  const [resourceFilter, setResourceFilter] = useState('');
  const [rangeStart, setRangeStart] = useState(() => isoDay(new Date()));
  const [rangeEnd, setRangeEnd] = useState(() => isoDay(new Date(Date.now() + 56 * 86400000))); // +8 weeks

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = await dvList<ProjTask>(pca, 'dagrun_pm_tasks', { select: TASK_FIELDS, filter: `_dagrun_pm_projectid_value eq ${projectId}`, top: 5000 });
      setTasks(t);
      const linked = !!project.dagrun_pm_useglobalresourcepool && !!project.dagrun_pm_resourcepoolid;
      const pm = linked ? await listPoolMembers(pca, project.dagrun_pm_resourcepoolid as string).catch(() => []) : [];
      setPoolMembers(pm);
    } finally { setLoading(false); }
  }, [pca, projectId, project.dagrun_pm_useglobalresourcepool, project.dagrun_pm_resourcepoolid]);
  useEffect(() => { load(); }, [load]);

  // ── B2: resource list = pool members (if linked) UNION distinct task
  //        assignees, de-duplicated by a STABLE id (userId), never by name. ──
  const resources = useMemo<Resource[]>(() => {
    const byId = new Map<string, Resource>();
    for (const m of poolMembers) {
      const uid = m.dagrun_pm_userid ?? '';
      if (uid) byId.set(uid, { userId: uid, name: m.dagrun_pm_username ?? m.dagrun_pm_name ?? uid, source: 'pool' });
    }
    for (const t of tasks) {
      const uid = t.dagrun_pm_assigneeid;
      if (uid && !byId.has(uid)) byId.set(uid, { userId: uid, name: t.dagrun_pm_assigneename ?? uid, source: 'local' });
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [poolMembers, tasks]);

  // Resolve each resource's work calendar (Block A fallback chain) — bulk.
  useEffect(() => {
    if (resources.length === 0) { setCalendars(new Map()); return; }
    let cancelled = false;
    resolveProjectCalendars(pca, projectId, resources.map(r => r.userId))
      .then(m => { if (!cancelled) setCalendars(m); })
      .catch(() => { if (!cancelled) setCalendars(new Map()); });
    return () => { cancelled = true; };
  }, [pca, projectId, resources]);

  // Capacity per person from the Block A service (never reimplemented here).
  const capacityFor = useCallback((uid: string): WorkCapacity => {
    const cal = calendars.get(uid);
    if (!cal) return { isWorkingDay: monFriWorkingDay, hoursPerDay: FALLBACK_HOURS_PER_DAY };
    return { isWorkingDay: (dayMs: number) => isWorkingDayFor(cal, dayMs), hoursPerDay: cal.hoursPerDay };
  }, [calendars]);

  // ── B3: Person Allocation — reuse computeWorkload, scoped to THIS project's
  //        active tasks; capacity from calendars; same overalloc/conflict logic. ──
  const workload: MemberWorkload[] = useMemo(() => {
    const start = new Date(`${rangeStart}T00:00:00Z`);
    const end = new Date(`${rangeEnd}T00:00:00Z`);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return [];
    const activeTasks = tasks.filter(t => t.dagrun_pm_status !== TaskStatus.Done && t.dagrun_pm_status !== TaskStatus.Cancelled);
    // Synthetic members so BOTH pool and local resources appear; capacity comes
    // from capacityFor (defaulthoursperday is unused by computeWorkload now).
    const members = resources.map(r => ({ dagrun_pm_resourcepoolmemberid: r.userId, dagrun_pm_userid: r.userId, dagrun_pm_username: r.name, dagrun_pm_name: r.name } as unknown as ResourcePoolMember));
    return computeWorkload(members, activeTasks as WorkloadTask[], {
      memberUserId: resourceFilter || null,
      projectId,
      rangeStart: start, rangeEnd: end,
    }, capacityFor);
  }, [tasks, resources, resourceFilter, rangeStart, rangeEnd, projectId, capacityFor]);

  const overCount = workload.filter(w => w.hasOverallocation).length;

  if (loading) return <div className="spinner" style={{ margin: '40px auto' }} />;

  return (
    <div>
      {/* Toggle + filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
          {(['allocation', 'breakdown'] as const).map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: '7px 14px', fontSize: 13, border: 'none', cursor: 'pointer',
              background: view === v ? 'var(--brand-mist)' : 'var(--surface)',
              color: view === v ? 'var(--brand)' : 'var(--ink-3)', fontWeight: view === v ? 600 : 400,
            }}>{v === 'allocation' ? 'Person Allocation' : 'Task Breakdown'}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <label style={fLabel}>Resource
          <select value={resourceFilter} onChange={e => setResourceFilter(e.target.value)} style={sel}>
            <option value="">All resources</option>
            {resources.map(r => <option key={r.userId} value={r.userId}>{r.name}{r.source === 'local' ? ' (local)' : ''}</option>)}
          </select>
        </label>
        {view === 'allocation' && (<>
          <label style={fLabel}>From<input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} style={sel} /></label>
          <label style={fLabel}>To<input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} style={sel} /></label>
        </>)}
      </div>

      {resources.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
          No resources on this project yet. Assign tasks or link a Global Resource Pool (Project Settings → Resources).
        </div>
      ) : view === 'allocation' ? (
        <AllocationView workload={workload} overCount={overCount} capacityFor={capacityFor} />
      ) : (
        <BreakdownView tasks={tasks} resources={resources} resourceFilter={resourceFilter} />
      )}
    </div>
  );
}

// ── Person Allocation (timeline) — reuses cellColors + the exact thresholds ──
function AllocationView({ workload, overCount, capacityFor }: { workload: MemberWorkload[]; overCount: number; capacityFor: (u: string) => WorkCapacity }) {
  const weekCount = workload[0]?.buckets.length ?? 0;
  return (
    <>
      <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 11, color: 'var(--ink-4)', flexWrap: 'wrap', alignItems: 'center' }}>
        <Swatch bg="#DCFCE7" label="Light" /><Swatch bg="#BBF7D0" label="Healthy" />
        <Swatch bg="#FDE68A" label="Near capacity" /><Swatch bg="#F87171" label="Over-allocated" />
        {overCount > 0 && <span style={{ marginLeft: 'auto', color: '#F87171' }}>■ {overCount} over-allocated</span>}
        <span style={overCount > 0 ? {} : { marginLeft: 'auto' }}>Weekly hours vs. capacity — this project only</span>
      </div>
      {workload.length === 0 || weekCount === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-4)' }}>
          No allocation in this range. Tasks need an assignee, an estimate, and start + due dates.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>
              <th style={{ ...headCell, position: 'sticky', left: 0, zIndex: 2, background: 'var(--surface)', minWidth: NAME_COL_W, textAlign: 'left' }}>Resource</th>
              {workload[0].buckets.map(b => (
                <th key={b.startMs} style={{ ...headCell, minWidth: WEEK_COL_W }}>{new Date(b.startMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</th>
              ))}
            </tr></thead>
            <tbody>
              {workload.map(row => (
                <tr key={row.userId || row.name}>
                  <td style={{ ...nameCell, position: 'sticky', left: 0, zIndex: 1, background: 'var(--surface)' }}>
                    <div style={{ fontWeight: 500, color: 'var(--ink)' }}>{row.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{Math.round(row.totalHours)}h · {capacityFor(row.userId).hoursPerDay}h/day</div>
                  </td>
                  {row.buckets.map(cell => {
                    const { bg, fg } = cellColors(cell);
                    const util = cell.capacity > 0 ? Math.round(cell.utilization * 100) : 0;
                    const title = cell.hours === 0 ? 'No allocation'
                      : `${Math.round(cell.hours)}h / ${Math.round(cell.capacity)}h capacity (${util}%)` + (cell.overallocated ? '\nOver-allocated' : '');
                    return (
                      <td key={cell.startMs} title={title} style={{ ...dataCell, background: bg, color: fg }}>
                        {cell.hours > 0 ? <span style={{ fontSize: 11, fontWeight: 600 }}>{util > 0 ? `${util}%` : `${Math.round(cell.hours)}h`}</span> : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Task Breakdown — per-assignee estimated / completed / remaining hours ──
function BreakdownView({ tasks, resources, resourceFilter }: { tasks: ProjTask[]; resources: Resource[]; resourceFilter: string }) {
  const [sortKey, setSortKey] = useState<'name' | 'est' | 'done' | 'rem'>('name');
  const [dir, setDir] = useState<1 | -1>(1);

  const rows = useMemo(() => {
    const nameById = new Map(resources.map(r => [r.userId, r.name]));
    const agg = new Map<string, { name: string; est: number; done: number; rem: number; count: number }>();
    for (const t of tasks) {
      const uid = t.dagrun_pm_assigneeid ?? '__unassigned';
      if (resourceFilter && uid !== resourceFilter) continue;
      const name = uid === '__unassigned' ? 'Unassigned' : (nameById.get(uid) ?? t.dagrun_pm_assigneename ?? uid);
      const a = agg.get(uid) ?? { name, est: 0, done: 0, rem: 0, count: 0 };
      a.est += t.dagrun_pm_estimatedhours ?? 0;
      a.done += t.dagrun_pm_effortcompletedhours ?? 0;
      a.rem += t.dagrun_pm_effortremaininghours ?? 0;
      a.count += 1;
      agg.set(uid, a);
    }
    const arr = [...agg.values()];
    arr.sort((x, y) => {
      const v = sortKey === 'name' ? x.name.localeCompare(y.name) : (x[sortKey] - y[sortKey]);
      return v * dir;
    });
    return arr;
  }, [tasks, resources, resourceFilter, sortKey, dir]);

  const totals = rows.reduce((acc, r) => ({ est: acc.est + r.est, done: acc.done + r.done, rem: acc.rem + r.rem, count: acc.count + r.count }), { est: 0, done: 0, rem: 0, count: 0 });
  const hdr = (key: typeof sortKey, label: string, align: 'left' | 'right' = 'right') => (
    <th style={{ ...headCell, textAlign: align, cursor: 'pointer' }} onClick={() => { if (sortKey === key) setDir(d => (d === 1 ? -1 : 1)); else { setSortKey(key); setDir(1); } }}>
      {label}{sortKey === key ? (dir === 1 ? ' ↑' : ' ↓') : ''}
    </th>
  );
  const num = (n: number) => Math.round(n * 10) / 10;

  return (
    <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
        <thead><tr>
          {hdr('name', 'Resource', 'left')}
          <th style={{ ...headCell, textAlign: 'right' }}>Tasks</th>
          {hdr('est', 'Estimated (h)')}{hdr('done', 'Completed (h)')}{hdr('rem', 'Remaining (h)')}
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.name} style={{ borderTop: '0.5px solid var(--border-light)' }}>
              <td style={{ padding: '9px 12px', color: 'var(--ink)' }}>{r.name}</td>
              <td style={{ padding: '9px 12px', textAlign: 'right', color: 'var(--ink-3)' }}>{r.count}</td>
              <td style={{ padding: '9px 12px', textAlign: 'right', color: 'var(--ink-2)' }}>{num(r.est)}</td>
              <td style={{ padding: '9px 12px', textAlign: 'right', color: 'var(--ink-2)' }}>{num(r.done)}</td>
              <td style={{ padding: '9px 12px', textAlign: 'right', color: 'var(--ink-2)' }}>{num(r.rem)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--ink-4)' }}>No tasks for this selection.</td></tr>}
        </tbody>
        {rows.length > 0 && (
          <tfoot><tr style={{ borderTop: '1px solid var(--border)' }}>
            <td style={{ padding: '9px 12px', fontWeight: 600, color: 'var(--ink)' }}>Total</td>
            <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600 }}>{totals.count}</td>
            <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600 }}>{num(totals.est)}</td>
            <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600 }}>{num(totals.done)}</td>
            <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 600 }}>{num(totals.rem)}</td>
          </tr></tfoot>
        )}
      </table>
    </div>
  );
}

const Swatch = ({ bg, label }: { bg: string; label: string }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: bg, display: 'inline-block' }} />{label}</span>
);
const fLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: 'var(--ink-4)' };
const sel: React.CSSProperties = { padding: '6px 9px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--surface)', color: 'var(--ink)' };
const headCell: React.CSSProperties = { padding: '9px 8px', fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-4)', borderBottom: '1px solid var(--border-light)', textAlign: 'center', whiteSpace: 'nowrap' };
const nameCell: React.CSSProperties = { padding: '8px 12px', borderBottom: '0.5px solid var(--border-light)' };
const dataCell: React.CSSProperties = { padding: '8px 6px', textAlign: 'center', borderBottom: '0.5px solid var(--border-light)', borderLeft: '0.5px solid var(--border-light)' };
